import { gql, useMutation } from "@apollo/client";
import { useFormContext, useWatch } from "react-hook-form";
import { useState } from "react";

import { client } from "../../../../contexts/ApolloProvider";
import TextInput from "../../../../components/TextInput";
import styles from "./index.module.scss";
import PageMarker from "../../../../components/ADP/PageMarker";
import { Text } from "../../../../components/Type";
import Button from "../../../../components/Button";
import SvgArrowRight from "../../../../components/Icons/general-interface-1/ArrowRight";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import NotificationBanner from "../../../NotificationBanner";
import { adpAuthRegNoOptions, dateNotInFutureFieldOptions, phoneExtensionFieldOptions, phoneNumberFieldOptions } from "../../../../utils/forms/validation";
import { isAuthorizer, isSalesRep, isMotionSupport } from "../../../../utils/user";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import SkipSignatureModal from "../../../ADP/SkipSignatureModal";
import Modal from "../../../../components/Modal";

const INITIATE_SIGNING = gql`
  mutation SignClientFile($input: SignClientFileInput) {
    signClientFile(input: $input) {
      url
    }
  }
`;

const Section4 = () => {
  const { state: { user }, } = useStoreContext();
  const { clientFile, formMode: mode } = useClientFileContext();
  const { trigger } = useFormContext();
  const authorizerLoggedIn = isAuthorizer(user);
  //const supportLoggedIn = isMotionSupport(user);
  const vendorLoggedIn = isSalesRep(user);
  const assessmentDate = useWatch({ name: "section4.authorizer-assessment-date", });
  const [isSkipModalOpen, setSkipModalOpen] = useState(false);
  const enableDocusign = isAuthorizer(user) && mode !== "waitingSigning" && clientFile.signing_authorizer && clientFile.signing_authorizer.id === user?.id && !isMotionSupport(user);
  const enableSkip = (isMotionSupport(user) || enableDocusign) && mode !== "waitingSigning";

  const [initiateSigning, { data, error, loading }] = useMutation(
    INITIATE_SIGNING,
    {
      onCompleted: (data) => {
        window.location.href = data.signClientFile.url;
      },
    }
  );

  const handleSigning = async () => {
    const result = await trigger(["section4"]);
    if (result) {
      const apolloClient = client;
      const response = await apolloClient.query({
        query: gql`
          query ClientForm($id: ID!) {
            clientForm(id: $id) {
              id
              form_data
            }
          }
        `,
        variables: {
          id: `${clientFile.client_form.id}`,
        },
        fetchPolicy: "no-cache",
      });

      const responseAssessmentDate = response.data.clientForm?.form_data?.section4?.["authorizer-assessment-date"];

      if (responseAssessmentDate === assessmentDate) {
        initiateSigning({
          variables: {
            input: {
              formId: clientFile.client_form.id,
            },
          },
        });
      } else {
        setTimeout(() => {
          initiateSigning({
            variables: {
              input: {
                formId: clientFile.client_form.id,
              },
            },
          });
        }, 1500);
      }
    }
  };

  const handleSkip = async() => {
    const result = await trigger(["section4"]);
    if (result) setSkipModalOpen(true);
  };

  return (
    <>
      <div className={styles.sideMargin}>
        <PageMarker page={12} total={13} />
        <div className={styles.flex50}>
          <strong>
            <p>Authorizer&apos;s Signature</p>
          </strong>
          <strong>
            <p>To be filled by Authorizer only</p>
          </strong>
        </div>
        <Text className={styles.margin}>
          I hereby certify that I have personally assessed the applicant named on
          this form in person, I have confirmed their eligibility for funding
          assistance in accordance with all ADP funding guidelines, I have
          authorized the equipment described on this form based on a comprehensive
          clinical assessment, and have taken all safety and environmental
          concerns into consideration. I have advised the applicant or their agent
          that (i) they may purchase the ADP approved equipment from the ADP
          Registered Vendor of their choice, and have provided a list of ADP
          Registered Vendors in the applicant&apos;s community for their use or
          (ii) have informed the applicant or their agent about the policies and
          procedures of the ADP Central Equipment Pool for High Technology
          Wheelchairs (CEP).
        </Text>
        <div className={styles.grid4949}>
          <TextInput
            section="section4"
            id="authorizer-last-name"
            label="Authorizer's Last Name"
            fieldOptions={{
              required: authorizerLoggedIn
                ? "This is a required field."
                : undefined,
            }}
            disabled={vendorLoggedIn}
            maxLength={30}
            readOnly={mode !== "initiateSigning"}
          />
          <TextInput
            section="section4"
            id="authorizer-first-name"
            label="Authorizer's First Name"
            fieldOptions={{
              required: authorizerLoggedIn
                ? "This is a required field."
                : undefined,
            }}
            disabled={vendorLoggedIn}
            maxLength={30}
            readOnly={mode !== "initiateSigning"}
          />
        </div>

        <div className={styles.grid4828}>
          <TextInput
            section="section4"
            id="authorizer-business-telephone-number"
            label="Business Telephone Number"
            placeholder="000-000-0000"
            fieldOptions={{
              ...phoneNumberFieldOptions,
              required: authorizerLoggedIn
                ? "This is a required field."
                : undefined,
            }}
            disabled={vendorLoggedIn}
            readOnly={mode !== "initiateSigning"}
          />
          <TextInput
            section="section4"
            id="authorizer-extension"
            label="Extension"
            placeholder="000"
            fieldOptions={phoneExtensionFieldOptions}
            disabled={vendorLoggedIn}
            readOnly={mode !== "initiateSigning"}
          />
        </div>

        <div className={styles.grid4949}>
          <TextInput
            section="section4"
            id="adp-authorizer-registration-number"
            label="ADP Authorizer Registration Number"
            fieldOptions={{
              ...adpAuthRegNoOptions,
              required: authorizerLoggedIn
                ? "This is a required field"
                : undefined,
            }}
            disabled={vendorLoggedIn}
            readOnly={mode !== "initiateSigning"}
          />
          <TextInput
            section="section4"
            id="authorizer-assessment-date"
            label="Assessment Date"
            placeholder="YYYY/MM/DD"
            fieldOptions={{
              ...dateNotInFutureFieldOptions,
              required: authorizerLoggedIn
                ? "This is a required field"
                : undefined,
            }}
            disabled={vendorLoggedIn}
            readOnly={mode !== "initiateSigning"}
          />
        </div>

        {(error) && (
          <NotificationBanner
            error
            title="Signing Error"
            content="There was a problem submitting your request. Please try again."
          />
        )}

          <Button
            className={styles.buttonMargin}
            disabled={!enableSkip || loading || data}
            secondary
            icon={<SvgArrowRight />}
            onClick={handleSkip}
            >
            Complete without E-Signature
          </Button>
          <Button
            className={styles.buttonMargin}
            disabled={!enableDocusign || loading || data}
            primary
            icon={<SvgArrowRight />}
            onClick={handleSigning}
          >
            {loading || data ? "Please wait..." : "Proceed to signatures via DocuSign"}
          </Button>
        </div>

        <Modal
          className={styles.modalUpload}
          title="Skip E-Signature and Submit Application"
          isOpen={isSkipModalOpen}
          onClose={() => { setSkipModalOpen(false); }}
          closeOnOverlay={false}
        >
          <SkipSignatureModal
            clientFileId={clientFile.id}
            clientFormId={clientFile.client_form.id}
            setSkipModalOpen={setSkipModalOpen}
          />
        </Modal>
    </>
  );
};

export default Section4;
